import React, { forwardRef } from 'react';
import { clsx } from '@mentimeter/ragnar-tailwind-config';
import { Clickable } from '../../clickable';
import { IconContainer } from '../icon-container';
import { iconButtonVariants } from './tw/IconButton.variants';
import { type IconButtonProps } from './IconButton.types';

export const IconButton = forwardRef<HTMLElement, IconButtonProps>(
  (
    {
      children,
      borderRadius,
      size,
      variant,
      disabled = false,
      className,
      ...rest
    }: IconButtonProps,
    forwardedRef,
  ) => {
    const state = 'state' in rest ? rest.state : undefined;
    const stateVariant = state ? (state as 'success' | 'error') : undefined;
    const hasBlockingStateVariant =
      stateVariant === 'success' || stateVariant === 'error';
    const hasState = Boolean(state);

    const defaultClasses = clsx([
      'group',
      'items-center',
      'justify-center',
      'focus-visible:z-1',
      'cursor-pointer',
      'transition',
      'ease-in-out',
      'duration-[0.1s]',
      'enabled:active:scale-90',
      'enabled:active:opacity-100',
      'enabled:hover:opacity-100',
      'disabled:cursor-not-allowed',
      'disabled:opacity-100',
      'disabled:hover:opacity-100',
      '[&:disabled]:data-[has-state="false"]:bg-disabled-strong',
      '[&:disabled]:data-[has-state="false"]:hover:bg-disabled-strong',
      '[&:disabled]:data-[has-state="false"]:focus-visible:bg-disabled-strong',
      '[&:disabled]:data-[has-state="false"]:text-on-disabled-strong',
    ]);

    const iconButtonClasses = iconButtonVariants({
      variant: hasBlockingStateVariant ? null : variant,
      borderRadius,
      size,
      state: !disabled ? stateVariant : null,
    });

    return (
      <Clickable
        ref={forwardedRef}
        type="button"
        disabled={hasState || disabled}
        data-has-state={!disabled && hasState}
        className={clsx(defaultClasses, iconButtonClasses, className)}
        {...rest}
      >
        <IconContainer state={!disabled ? state : null} size={size}>
          {children}
        </IconContainer>
      </Clickable>
    );
  },
);
